/**
 * Los Angeles County electricity grid carbon intensity: weighted blend of
 * LADWP (City of LA) and the **rest of the county**.
 *
 * The “rest” intensity is whatever `carbonIntensityService` would use for the county **before**
 * blending: `lse_carbon_intensity` (CCA Power Content Label) if present, else the first
 * `ccas.egrid_match*` utility in `egrid_utilities` — for LA County that is typically **SCE eGRID**.
 *
 * Weight = (City of LA res+com kWh) / (Los Angeles County res+com kWh) when both are available;
 * otherwise DEFAULT_LADWP_KWH_SHARE (~half of county load is often LADWP-served).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabaseClient } from './supabase';

/** Fallback when county or city kWh totals are missing (order-of-magnitude typical share). */
export const DEFAULT_LADWP_KWH_SHARE = 0.5;

/** County total rows use geofips 6037 and geography_type county (location_name is often "Los Angeles" in source data). */
const LA_COUNTY_GEOFIPS_VARIANTS = ['6037', '06037'];

const LA_CITY_GEOFIPS_VARIANTS = ['644000', '0644000'];

/**
 * LADWP in eGRID / `egrid_utilities`. Use **utility_id** — `utility_name` varies by year
 * (e.g. 2007/2009 "Los Angeles City of" vs later "Los Angeles Department of Water & Power").
 */
export const LADWP_EGRID_UTILITY_ID = 11208;

/** When a year has multiple subregions, prefer CAMX (California grid). */
const PREFERRED_LADWP_SUBREGION = 'CAMX';

function pickLadwpEgridRow(
  rows: Array<{
    gco2e_per_kwh?: unknown;
    subregion?: unknown;
    net_generation?: unknown;
  }>
): { gco2e_per_kwh?: unknown } | null {
  if (!rows.length) return null;
  const camx = rows.filter(
    (r) => String(r.subregion ?? '').trim().toUpperCase() === PREFERRED_LADWP_SUBREGION
  );
  const pool = camx.length ? camx : rows;
  let best = pool[0];
  let bestNg = Number(best.net_generation);
  for (let i = 1; i < pool.length; i++) {
    const r = pool[i];
    const ng = Number(r.net_generation);
    if (Number.isFinite(ng) && Number.isFinite(bestNg) && ng > bestNg) {
      best = r;
      bestNg = ng;
    } else if (!Number.isFinite(bestNg) && Number.isFinite(ng)) {
      best = r;
      bestNg = ng;
    }
  }
  return best;
}

let shareByYearCache: Map<number, number> | null = null;
let ladwpIntensityByYearCache: Map<number, number | null> | null = null;

export function clearLosAngelesCountyGridCiCaches(): void {
  shareByYearCache = null;
  ladwpIntensityByYearCache = null;
}

export function isLosAngelesCountyGeofips(geofips: string | number | null | undefined): boolean {
  if (geofips === null || geofips === undefined) return false;
  const raw = String(geofips).replace(/\.0+$/, '').trim();
  const stripped = raw.replace(/^0+/, '') || '0';
  // County FIPS 06037 → stored as 6037, 06037, etc.
  return stripped === '6037' || stripped === '60037';
}

/** Los Angeles County county-level res+com kWh (one row per sector in elec_*_format). */
async function fetchLosAngelesCountyTotalKwh(year: number, client: SupabaseClient = supabaseClient): Promise<number> {
  const { data: resRows, error: e1 } = await client
    .from('elec_res_format')
    .select('total_kwh')
    .eq('year', year)
    .eq('sector', 'Residential')
    .eq('geography_type', 'county')
    .in('geofips', LA_COUNTY_GEOFIPS_VARIANTS);
  const { data: comRows, error: e2 } = await client
    .from('elec_com_format')
    .select('total_kwh')
    .eq('year', year)
    .eq('sector', 'Commercial')
    .eq('geography_type', 'county')
    .in('geofips', LA_COUNTY_GEOFIPS_VARIANTS);
  let res = 0;
  for (const row of resRows ?? []) {
    const v = Number((row as { total_kwh?: unknown }).total_kwh);
    if (Number.isFinite(v)) res += v;
  }
  let com = 0;
  for (const row of comRows ?? []) {
    const v = Number((row as { total_kwh?: unknown }).total_kwh);
    if (Number.isFinite(v)) com += v;
  }
  return res + com;
}

/** City of Los Angeles place rows (LADWP); geofips variants for Supabase storage. */
async function fetchLosAngelesCityTotalKwh(year: number, client: SupabaseClient = supabaseClient): Promise<number> {
  const { data: resRows, error: e1 } = await client
    .from('elec_res_format')
    .select('total_kwh')
    .eq('year', year)
    .eq('sector', 'Residential')
    .in('geofips', LA_CITY_GEOFIPS_VARIANTS);
  const { data: comRows, error: e2 } = await client
    .from('elec_com_format')
    .select('total_kwh')
    .eq('year', year)
    .eq('sector', 'Commercial')
    .in('geofips', LA_CITY_GEOFIPS_VARIANTS);
  let res = 0;
  for (const row of resRows ?? []) {
    const v = Number((row as { total_kwh?: unknown }).total_kwh);
    if (Number.isFinite(v)) res += v;
  }
  let com = 0;
  for (const row of comRows ?? []) {
    const v = Number((row as { total_kwh?: unknown }).total_kwh);
    if (Number.isFinite(v)) com += v;
  }
  return res + com;
}

export async function fetchLadwpKwhShareForYear(
  year: number,
  client: SupabaseClient = supabaseClient
): Promise<number> {
  const [county, city] = await Promise.all([
    fetchLosAngelesCountyTotalKwh(year, client),
    fetchLosAngelesCityTotalKwh(year, client),
  ]);
  if (county > 0 && city >= 0) {
    const f = city / county;
    if (Number.isFinite(f)) return Math.min(1, Math.max(0, f));
  }
  return DEFAULT_LADWP_KWH_SHARE;
}

/** LADWP-specific gCO2e/kWh from egrid_utilities (by utility_id, not name). */
export async function fetchLadwpEgridGPerKwhForYear(
  year: number,
  client: SupabaseClient = supabaseClient
): Promise<number | null> {
  const { data, error } = await client
    .from('egrid_utilities')
    .select('gco2e_per_kwh, utility_name, subregion, net_generation')
    .eq('year', year)
    .eq('utility_id', LADWP_EGRID_UTILITY_ID);

  if (error || !data?.length) {
    return null;
  }
  const row = pickLadwpEgridRow(data);
  if (!row) return null;
  const v = Number(row.gco2e_per_kwh);
  return Number.isFinite(v) ? v : null;
}

/**
 * Linear interpolation of interior nulls along a sorted year axis (in place).
 * Multi-pass so chains of missing years fill correctly.
 */
export function fillInteriorLinearGapsByYearInPlace(
  years: number[],
  values: (number | null | undefined)[]
): void {
  if (years.length !== values.length || years.length === 0) return;
  for (let pass = 0; pass < years.length; pass++) {
    let changed = false;
    for (let i = 0; i < years.length; i++) {
      const v = values[i];
      if (v != null && Number.isFinite(Number(v))) continue;
      let pi = -1;
      let ni = -1;
      for (let j = i - 1; j >= 0; j--) {
        const w = values[j];
        if (w != null && Number.isFinite(Number(w))) {
          pi = j;
          break;
        }
      }
      for (let j = i + 1; j < years.length; j++) {
        const w = values[j];
        if (w != null && Number.isFinite(Number(w))) {
          ni = j;
          break;
        }
      }
      if (pi >= 0 && ni >= 0) {
        const y = years[i];
        const y0 = years[pi];
        const y1 = years[ni];
        const v0 = Number(values[pi]);
        const v1 = Number(values[ni]);
        if (y1 !== y0) {
          values[i] = v0 + ((y - y0) / (y1 - y0)) * (v1 - v0);
          changed = true;
        }
      }
    }
    if (!changed) break;
  }
}

/**
 * After interior gap-fill, carry the first/last known values outward so **tail years**
 * (e.g. 2024 before eGRID release) still get an LADWP intensity. Without this, LA County
 * blend is skipped for those years and the chart shows raw extrapolated “rest” only (~200s).
 */
export function fillExteriorCarryFromAnchorsInPlace(values: (number | null | undefined)[]): void {
  let fi = -1;
  let la = -1;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v != null && Number.isFinite(Number(v))) {
      fi = i;
      break;
    }
  }
  for (let i = values.length - 1; i >= 0; i--) {
    const v = values[i];
    if (v != null && Number.isFinite(Number(v))) {
      la = i;
      break;
    }
  }
  if (fi < 0 || la < 0) return;
  const firstVal = Number(values[fi]);
  const lastVal = Number(values[la]);
  for (let i = 0; i < fi; i++) values[i] = firstVal;
  for (let i = la + 1; i < values.length; i++) values[i] = lastVal;
}

function fillLadwpIntensityGapsInCache(sortedYears: number[], cache: Map<number, number | null>): void {
  const vals = sortedYears.map((y) => {
    const v = cache.get(y);
    return v != null && Number.isFinite(v) ? v : null;
  });
  fillInteriorLinearGapsByYearInPlace(sortedYears, vals);
  fillExteriorCarryFromAnchorsInPlace(vals);
  sortedYears.forEach((y, i) => {
    const v = vals[i];
    if (v != null && Number.isFinite(v)) cache.set(y, v);
  });
}

/** Merge any missing years into in-memory caches (browser / repeated chart loads). */
export async function ensureLaCountyCachesWarm(years: number[]): Promise<void> {
  const uniq = Array.from(new Set(years)).filter((y) => Number.isFinite(y));
  if (!shareByYearCache) shareByYearCache = new Map();
  if (!ladwpIntensityByYearCache) ladwpIntensityByYearCache = new Map();
  const needShare = uniq.filter((y) => !shareByYearCache!.has(y));
  const needLadwp = uniq.filter((y) => !ladwpIntensityByYearCache!.has(y));
  if (needShare.length > 0) {
    await Promise.all(
      needShare.map(async (y) => {
        shareByYearCache!.set(y, await fetchLadwpKwhShareForYear(y));
      })
    );
  }
  if (needLadwp.length > 0) {
    await Promise.all(
      needLadwp.map(async (y) => {
        ladwpIntensityByYearCache!.set(y, await fetchLadwpEgridGPerKwhForYear(y));
      })
    );
  }

  // eGRID often omits odd years for LADWP; without this, LA blend skips those years and the chart
  // shows raw interpolated SCE (~170) between blended neighbors (~340) — not a linear *display* series.
  const sorted = [...uniq].sort((a, b) => a - b);
  fillLadwpIntensityGapsInCache(sorted, ladwpIntensityByYearCache);
}

/**
 * Short label for the **non–City-of-LA** share of the county mix — i.e. the intensity
 * from `fetchCarbonIntensityData` *before* the LA County LADWP blend (CCA PCL or eGRID match, usually SCE).
 */
export function shortLabelForCountyRestCarbonIntensity(preBlendProvider: string): string {
  const p = (preBlendProvider || '').trim();
  const low = p.toLowerCase();
  if (!p || low === 'extrapolated') return 'SCE eGRID (extrapolated)';
  if (low === 'interpolated') return 'SCE eGRID (interpolated)';
  if (low.includes('power content label')) {
    const name = p.split(' - ')[0].trim();
    return name ? `${name} (Power Content Label)` : 'CCA (Power Content Label)';
  }
  if (low.includes('southern california edison')) {
    return 'SCE eGRID';
  }
  if (low.includes('eia-861') && low.includes('egrid')) {
    const head = p.split(' - ')[0].trim();
    return head ? `${head} (eGRID)` : 'eGRID utility';
  }
  return p || 'county grid (eGRID/CCA)';
}

/**
 * Blend county “rest” intensity (from ccas / eGRID — typically SCE for LA County) with LADWP eGRID.
 */
function buildLaCountyBlendProviderLabel(
  f: number,
  ladwp: number,
  restOfCountyGPerKwh: number,
  preBlendProvider: string
): string {
  const restLabel = shortLabelForCountyRestCarbonIntensity(preBlendProvider);
  return `LA County weighted: ${(f * 100).toFixed(1)}% LADWP eGRID (${ladwp.toFixed(0)} g/kWh), ${((1 - f) * 100).toFixed(1)}% ${restLabel} (~${restOfCountyGPerKwh.toFixed(0)} g/kWh)`;
}

/**
 * Optional `supabase` (e.g. service-role client in Node scripts) bypasses the browser cache
 * so RLS and credentials match the caller.
 */
export async function blendLosAngelesCountyCarbonIntensityGPerKwh(
  year: number,
  restOfCountyGPerKwh: number,
  yearsForCache?: number[],
  supabase?: SupabaseClient,
  /** e.g. `Southern California Edison Co - EIA-861 & eGRID` from the county ccas/eGRID path */
  restOfCountyPreBlendProvider?: string | null
): Promise<{ value: number; provider: string } | null> {
  const pre = restOfCountyPreBlendProvider ?? '';
  if (supabase) {
    const [fRaw, ladwp] = await Promise.all([
      fetchLadwpKwhShareForYear(year, supabase),
      fetchLadwpEgridGPerKwhForYear(year, supabase),
    ]);
    if (ladwp == null || !Number.isFinite(ladwp)) return null;
    const f = fRaw;
    const blended = f * ladwp + (1 - f) * restOfCountyGPerKwh;
    return {
      value: blended,
      provider: buildLaCountyBlendProviderLabel(f, ladwp, restOfCountyGPerKwh, pre),
    };
  }
  const ys = yearsForCache ?? [year];
  await ensureLaCountyCachesWarm(ys);
  const ladwp = ladwpIntensityByYearCache?.get(year);
  if (ladwp == null || !Number.isFinite(ladwp)) return null;
  const f = shareByYearCache?.get(year) ?? DEFAULT_LADWP_KWH_SHARE;
  const blended = f * ladwp + (1 - f) * restOfCountyGPerKwh;
  return {
    value: blended,
    provider: buildLaCountyBlendProviderLabel(f, ladwp, restOfCountyGPerKwh, pre),
  };
}

export type LaCountyCombinedPoint = {
  value: number;
  source: string;
  provider: string;
};

/**
 * Bar color after LA County blend should reflect how the **non-LADWP (rest)** series was derived
 * (eGRID / PCL path in `fetchCarbonIntensityData`), not force everything to “observed” blue.
 */
export function laCountyBlendedBarSourceFromPreBlend(preBlendSource: string): 'observed' | 'interpolated' | 'extrapolated' {
  const s = (preBlendSource || '').toLowerCase();
  if (s === 'extrapolated') return 'extrapolated';
  if (s === 'interpolated') return 'interpolated';
  // 'observed', 'cca', or unknown → weighted blend from real anchors
  return 'observed';
}

/** Apply LADWP + balance blend in place (Carbon Intensity chart). */
export async function applyLaCountyBlendToCombinedSeries(
  years: number[],
  combinedData: LaCountyCombinedPoint[]
): Promise<void> {
  await ensureLaCountyCachesWarm(years);
  if (!shareByYearCache || !ladwpIntensityByYearCache) return;
  for (let i = 0; i < years.length; i++) {
    const y = years[i];
    const ladwp = ladwpIntensityByYearCache.get(y);
    if (ladwp == null || !Number.isFinite(ladwp)) continue;
    const f = shareByYearCache.get(y) ?? DEFAULT_LADWP_KWH_SHARE;
    const rest = combinedData[i].value;
    const preBlendProvider = combinedData[i].provider;
    const preBlendSource = combinedData[i].source;
    combinedData[i] = {
      value: f * ladwp + (1 - f) * rest,
      source: laCountyBlendedBarSourceFromPreBlend(preBlendSource),
      provider: buildLaCountyBlendProviderLabel(f, ladwp, rest, preBlendProvider),
    };
  }
}

/** Same as `applyLaCountyBlendToCombinedSeries` but uses the given Supabase client (Node scripts). */
export async function applyLaCountyBlendToCombinedSeriesWithClient(
  years: number[],
  combinedData: LaCountyCombinedPoint[],
  client: SupabaseClient
): Promise<void> {
  const [shares, ladwpsRaw] = await Promise.all([
    Promise.all(years.map((y) => fetchLadwpKwhShareForYear(y, client))),
    Promise.all(years.map((y) => fetchLadwpEgridGPerKwhForYear(y, client))),
  ]);
  const ladwps: (number | null)[] = [...ladwpsRaw];
  fillInteriorLinearGapsByYearInPlace(years, ladwps);
  fillExteriorCarryFromAnchorsInPlace(ladwps);
  for (let i = 0; i < years.length; i++) {
    const ladwp = ladwps[i];
    if (ladwp == null || !Number.isFinite(ladwp)) continue;
    const f = shares[i];
    const rest = combinedData[i].value;
    const preBlendProvider = combinedData[i].provider;
    const preBlendSource = combinedData[i].source;
    combinedData[i] = {
      value: f * ladwp + (1 - f) * rest,
      source: laCountyBlendedBarSourceFromPreBlend(preBlendSource),
      provider: buildLaCountyBlendProviderLabel(f, ladwp, rest, preBlendProvider),
    };
  }
}
